import { useCallback, useMemo, useState } from 'react';

export const COLLAPSED_ADMIN_GROUPS_STORAGE_KEY = 'ui.adminNav.collapsedGroups@v1';

export type CollapsedGroupsStorage = Pick<Storage, 'getItem' | 'setItem'>;

function toCollapsedGroupsStorage(value: unknown): CollapsedGroupsStorage | null {
  if (!value || typeof value !== 'object') return null;
  const maybe = value as { getItem?: unknown; setItem?: unknown };
  if (typeof maybe.getItem !== 'function') return null;
  if (typeof maybe.setItem !== 'function') return null;
  return value as CollapsedGroupsStorage;
}

/**
 * Stores which groups are *collapsed* rather than which are open, so the empty state means
 * "everything expanded" — the default — and any group added later starts expanded too.
 */
export function getCollapsedGroupsFromStorage(storage: CollapsedGroupsStorage | null): string[] {
  if (!storage) return [];

  try {
    const raw = storage.getItem(COLLAPSED_ADMIN_GROUPS_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((key): key is string => typeof key === 'string');
  } catch {
    return [];
  }
}

export function setCollapsedGroupsInStorage(
  storage: CollapsedGroupsStorage | null,
  value: string[]
): void {
  if (!storage) return;

  try {
    storage.setItem(COLLAPSED_ADMIN_GROUPS_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // best-effort
  }
}

export function useCollapsedAdminGroups(storageOverride?: CollapsedGroupsStorage | null) {
  const storage = useMemo(() => {
    if (storageOverride !== undefined) return storageOverride;
    if (typeof window === 'undefined') return null;
    return toCollapsedGroupsStorage(window.localStorage);
  }, [storageOverride]);

  const [collapsed, setCollapsed] = useState<string[]>(() =>
    getCollapsedGroupsFromStorage(storage)
  );

  const toggleGroup = useCallback(
    (key: string) => {
      setCollapsed((prev) => {
        const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
        setCollapsedGroupsInStorage(storage, next);
        return next;
      });
    },
    [storage]
  );

  const isCollapsed = useCallback((key: string) => collapsed.includes(key), [collapsed]);

  return { collapsed, isCollapsed, toggleGroup };
}
